import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useNavigate, useOutletContext, useParams, useSearchParams } from '@remix-run/react';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast from '~/hooks/useToast';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Publication } from '~/interfaces/publication';

enum ToastStatus {
  SUCCESS = 'success',
  ERROR = 'error',
}

type Props = {
  publication: Publication;
  showSubmitButton?: boolean;
  onSuccess?: () => void;
  children?: ({ isLoading }: { isLoading: boolean }) => ReactNode;
};

const Unsubscribe = ({ publication, children, onSuccess, showSubmitButton = true }: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher();
  const params = useParams();
  const { subscriberId } = params;
  const fetcherData = fetcher.data as {
    postUnsubscribeFormId: string;
    toast: { status: ToastStatus; message: string };
  };
  const isSubmitting = fetcher.state === 'submitting';
  const { removeCookie, cookies } = useOutletContext<OutletContext>();
  const status = fetcherData?.toast?.status;
  const navigate = useNavigate();

  useEffect(() => {
    if (fetcherData?.postUnsubscribeFormId) {
      navigate(`/forms/${fetcherData?.postUnsubscribeFormId}`);
    }
  }, [fetcherData?.postUnsubscribeFormId, navigate]);

  useToast(fetcherData?.toast);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want this to run on every render
  useEffect(() => {
    if (status === 'success') {
      removeCookie('active_upgrade');
      onSuccess?.();
    }
  }, [status]);

  return (
    <fetcher.Form method="post" action="/unsubscribe">
      <input hidden name="subscriber_id" defaultValue={subscriberId} />
      <input hidden name="unsubscribe_form_id" defaultValue={publication.post_unsubscribe_form_id || ''} />
      {searchParams.has('post_id') && <input hidden name="post_id" defaultValue={searchParams.get('post_id') || ''} />}
      {searchParams.has('newsletter_list_id') && (
        <input hidden name="newsletter_list_id" defaultValue={searchParams.get('newsletter_list_id') || ''} />
      )}
      <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid ?? ''} />
      <StatusInputs
        name="unsubscribe"
        errorMessage={t('account_actions.downgrade.toast.error')}
        successMessage={t('account_actions.downgrade.toast.unsubscribed')}
      />
      {showSubmitButton && (
        <Button type="submit" variant="danger" isLoading={isSubmitting} btnSize="md">
          {isSubmitting ? `${t('shared.actions.unsubscribing')}…` : t('shared.cta.unsubscribe')}
        </Button>
      )}
      {children?.({ isLoading: isSubmitting })}
    </fetcher.Form>
  );
};

export default Unsubscribe;
